/**
 * Embed Conversation Container
 * Displays conversation messages in the embed widget.
 * Markdown is rendered + sanitised once by the shared renderMarkdown helper.
 */

import { BookOpen, FileText, Globe, Image as ImageIcon } from 'lucide-react';
import React, { useMemo } from 'react';

import { recordPublicChipTap } from '@/components/brain-card/cognition-chip-tap';
import type {
  CognitionChip,
  NextStepIntent,
  TurnSource,
} from '@/components/brain-card/useCognitionAskStream';
import { formatMessageTime } from '@/components/universal-messaging/utils/dateFormatting';
import { renderMarkdown } from '@/components/universal-messaging/utils/markdownRenderer';
import { useStickToBottom } from '@/ux/hooks/useStickToBottom';

import { useAdvancedConfig } from '../hooks/useAdvancedConfig';
import { AgentIconStyle, useEmbedConfig } from '../providers/EmbedConfigProvider';
import { EmbedCognitionMessage } from '../services/EmbedGuestMessageService';
import { useEmbedTheme } from '../theme';

// ============================================================================
// Agent Icon Components
// ============================================================================

interface AgentIconProps {
  style: AgentIconStyle;
  color: string;
  size?: number;
}

const AgentIcon: React.FC<AgentIconProps> = ({ style, color, size = 20 }) => {
  switch (style) {
    case 'robot':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="11" width="18" height="10" rx="2" />
          <circle cx="12" cy="5" r="2" />
          <path d="M12 7v4" />
          <line x1="8" y1="16" x2="8" y2="16" />
          <line x1="16" y1="16" x2="16" y2="16" />
        </svg>
      );
    case 'sparkle':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
          <path d="M5 19l1 3 1-3 3-1-3-1-1-3-1 3-3 1 3 1z" />
          <path d="M19 13l0.5 1.5L21 15l-1.5 0.5L19 17l-0.5-1.5L17 15l1.5-0.5L19 13z" />
        </svg>
      );
    case 'brain':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24A2.5 2.5 0 0 1 9.5 2" />
          <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24A2.5 2.5 0 0 0 14.5 2" />
        </svg>
      );
    case 'chat':
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M8 10h.01" />
          <path d="M12 10h.01" />
          <path d="M16 10h.01" />
        </svg>
      );
    case 'message':
    default:
      // Message icon (default) - simple chat bubble
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      );
  }
};

// ============================================================================
// Types
// ============================================================================

interface EmbedConversationContainerProps {
  messages: EmbedCognitionMessage[];
  mediaBaseUrl: string;
  cognitionName?: string;
  cognitionImage?: string;
  chips?: CognitionChip[];
  chipsIntent?: NextStepIntent;
  clarifyingQuestionText?: string;
  onChipClick?: (prompt: string) => void;
  // Live tool-use label (e.g. "Searching the web…") attached to the in-flight
  // loading bubble while the model is between rounds. Cleared on turn complete.
  liveToolLabel?: string | null;
  onRetry?: (message: EmbedCognitionMessage) => void;
}

// ============================================================================
// Loading Dots Component
// ============================================================================

const ToolPill: React.FC<{ label: string; followedByText?: boolean }> = ({ label, followedByText }) => {
  const { theme, primaryColor } = useEmbedTheme();
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 10px 4px 6px',
        borderRadius: theme.borderRadius.full,
        backgroundColor: `${primaryColor}14`,
        color: theme.colors.text.link,
        fontSize: theme.typography.fontSize.xs,
        fontWeight: theme.typography.fontWeight.medium,
        marginBottom: followedByText ? '6px' : 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '12px',
          height: '12px',
          border: `2px solid ${primaryColor}40`,
          borderTopColor: primaryColor,
          borderRadius: '50%',
          animation: 'embed-spin 0.8s linear infinite',
        }}
      />
      {label}
    </div>
  );
};

const LoadingDots: React.FC<{ forUserBubble?: boolean }> = ({ forUserBubble }) => {
  const { theme, primaryColor } = useEmbedTheme();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 0' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: theme.borderRadius.full,
            backgroundColor: forUserBubble ? theme.colors.text.inverse : primaryColor,
            opacity: 0.6,
            animation: `embed-bounce 1.4s ${i * 0.16}s infinite ease-in-out both`,
          }}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Message Bubble Component
// ============================================================================

interface MessageBubbleProps {
  message: EmbedCognitionMessage;
  mediaBaseUrl: string;
  cognitionName?: string;
  cognitionImage?: string;
  liveToolLabel?: string | null;
  onRetry?: (message: EmbedCognitionMessage) => void;
}

// Helper to safely append media URL, avoiding double URLs and ensuring proper slashes
function appendMediaUrl(url: string | undefined | null, mediaBaseUrl: string): string | null {
  if (!url) return null;
  // If already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Ensure proper slash between base URL and path
  const base = mediaBaseUrl.endsWith('/') ? mediaBaseUrl : `${mediaBaseUrl}/`;
  const path = url.startsWith('/') ? url.slice(1) : url;
  return `${base}${path}`;
}

// ============================================================================
// Source chips — titled provenance under an answer (titles only, never ids)
// ============================================================================

const SOURCE_KIND_ICONS: Record<string, typeof FileText> = {
  file: FileText,
  image: ImageIcon,
  portal: BookOpen,
  brain_doc: BookOpen,
  web: Globe,
};
const MAX_VISIBLE_SOURCES = 4;

const SourceChips: React.FC<{ sources: TurnSource[] }> = ({ sources }) => {
  const { theme, styles, utils } = useEmbedTheme();
  const titled = sources.filter((source) => source.title.trim().length > 0);
  if (titled.length === 0) return null;
  const visible = titled.slice(0, MAX_VISIBLE_SOURCES);
  const hidden = titled.length - visible.length;
  const chipStyle = utils.merge(styles.text({ variant: 'tertiary', size: 'xs' }), {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    maxWidth: '16rem',
    padding: '2px 8px',
    borderRadius: '999px',
    border: `1px solid ${theme.colors.border.subtle}`,
    backgroundColor: theme.colors.background.elevated,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });
  return (
    <ul
      aria-label="Sources"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        listStyle: 'none',
        margin: '8px 0 0',
        padding: 0,
      }}
    >
      {visible.map((source) => {
        const Icon = SOURCE_KIND_ICONS[source.kind] ?? FileText;
        return (
          <li key={source.ref || source.title} title={source.title} style={chipStyle}>
            <Icon size={10} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{source.title}</span>
          </li>
        );
      })}
      {hidden > 0 && <li style={styles.text({ variant: 'tertiary', size: 'xs' })}>+{hidden} more</li>}
    </ul>
  );
};

function renderBubbleContents({
  isLoading,
  isUser,
  messageText,
  renderedHtml,
  liveToolLabel,
}: {
  isLoading: boolean | undefined;
  isUser: boolean;
  messageText: string;
  renderedHtml: string;
  liveToolLabel?: string | null;
}) {
  if (messageText) {
    return (
      <div
        className={`embed-msg-content${isLoading ? ' embed-msg-content--streaming' : ''}`}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
      />
    );
  }
  if (isLoading && !liveToolLabel) return <LoadingDots forUserBubble={isUser} />;
  return null;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  mediaBaseUrl,
  cognitionName,
  cognitionImage,
  liveToolLabel,
  onRetry,
}) => {
  const { theme, styles, utils, primaryColor } = useEmbedTheme();
  const { agentIcon, chatStyle = 'bubbles' } = useEmbedConfig();
  const { styles: advancedStyles, config: advancedConfig } = useAdvancedConfig();

  const isUser = message.messageType === 'USER';
  const isLoading = message.loading;
  const isError = !isUser && message.cognitionSuccess === false;

  const displayName = isUser ? 'You' : message.cognition?.name || cognitionName || 'AI';

  const getAvatarUrl = (): string | null => {
    if (isUser) {
      // Always use generic user icon for user messages in embed widget
      return null;
    }
    const img = message.cognition?.displayImage || cognitionImage;
    return appendMediaUrl(img, mediaBaseUrl);
  };

  const avatarUrl = getAvatarUrl();

  // Concatenate the message text. Bot replies sometimes ship the body inside
  // contents[], so we have to look there when message.message is empty.
  const messageText = useMemo((): string => {
    if (message.message) return message.message;
    if (message.contents && message.contents.length > 0) {
      const textContents = message.contents
        .filter((c: any) => c.contentType === 'text' && c.content?.text)
        .map((c: any) => c.content.text)
        .join('\n\n');
      if (textContents) return textContents;
    }
    return '';
  }, [message.message, message.contents]);

  // renderMarkdown already DOMPurifies and emits target="_blank"/rel on links.
  // Memoise so streaming token deltas don't re-parse the whole markdown tree
  // on every render — marked + hljs runs once per actual text change.
  const renderedHtml = useMemo(() => renderMarkdown(messageText), [messageText]);

  // Determine if avatar should be shown
  const showAvatar = isUser ? advancedStyles.userAvatar.show : advancedStyles.agentAvatar.show;
  const avatarStyle = isUser ? advancedStyles.userAvatar : advancedStyles.agentAvatar;

  // Get bubble style from advanced config
  const bubbleStyle = isUser ? advancedStyles.userBubble : advancedStyles.agentBubble;

  // Chat-style preset (bubbles | minimal | cards). `minimal` strips the bubble
  // background to plain text; `cards` swaps the solid bubble for a bordered card.
  const userR = advancedConfig.messageBubbles?.user?.borderRadius ?? 1;
  const agentR = advancedConfig.messageBubbles?.agent?.borderRadius ?? 1;
  const bubbleRadius =
    chatStyle === 'minimal'
      ? '0'
      : chatStyle === 'cards'
      ? theme.borderRadius.lg
      : isUser
      ? `${userR}rem ${userR}rem 4px ${userR}rem`
      : `${agentR}rem ${agentR}rem ${agentR}rem 4px`;
  const chatStyleSurface =
    chatStyle === 'minimal'
      ? {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: 'none',
          padding: '2px 0',
          color: theme.colors.text.primary,
        }
      : chatStyle === 'cards'
      ? {
          backgroundColor: isUser ? `${primaryColor}14` : theme.colors.background.elevated,
          border: `1px solid ${isUser ? `${primaryColor}40` : theme.colors.border.subtle}`,
          boxShadow: theme.shadow.sm,
          color: theme.colors.text.primary,
        }
      : {};

  // Avatar gap from config
  const avatarGap = advancedConfig.avatar?.gap || 8;

  return (
    <div
      style={{
        display: 'flex',
        gap: `${avatarGap}px`,
        flexDirection: isUser ? 'row-reverse' : 'row',
        marginBottom: `${advancedStyles.messageGap}px`,
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar - conditionally rendered based on advanced config */}
      {showAvatar && (
        <div
          style={{
            ...avatarStyle,
            backgroundColor: isUser ? primaryColor : theme.colors.background.elevated,
          }}
        >
          {isUser ? (
            <svg
              width={Math.min(avatarStyle.width ? parseInt(String(avatarStyle.width)) * 0.6 : 20, 24)}
              height={Math.min(avatarStyle.height ? parseInt(String(avatarStyle.height)) * 0.6 : 20, 24)}
              viewBox="0 0 24 24"
              fill="none"
              stroke={theme.colors.text.inverse}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          ) : (
            <AgentIcon
              style={agentIcon || 'robot'}
              color={theme.colors.text.secondary}
              size={Math.min(avatarStyle.width ? parseInt(String(avatarStyle.width)) * 0.6 : 20, 24)}
            />
          )}
        </div>
      )}

      {/* Message Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          gap: theme.spacing['1'],
          maxWidth: bubbleStyle.maxWidth || '75%',
        }}
      >
        {/* Name */}
        <span
          style={utils.merge(styles.text({ variant: 'secondary', size: 'sm' }), {
            fontWeight: theme.typography.fontWeight.medium,
            marginLeft: isUser ? 'auto' : theme.spacing['1'],
            marginRight: isUser ? theme.spacing['1'] : 'auto',
          })}
        >
          {displayName}
        </span>

        {/* Bubble - using advanced config styles */}
        <div
          style={{
            ...bubbleStyle,
            // Bubble sizes to its own content; column wrapper enforces the overall max width.
            maxWidth: '100%',
            width: 'fit-content',
            minWidth: chatStyle === 'minimal' ? 0 : '60px',
            // Chat-style preset surface (minimal = plain text, cards = bordered card).
            ...chatStyleSurface,
            ...(isError
              ? {
                  borderColor: `${theme.colors.border.error}`,
                  backgroundColor: `${theme.colors.text.error}0d`,
                  color: theme.colors.text.primary,
                }
              : null),
            // Border radius: tail effect for bubbles, flat for minimal, uniform for cards.
            borderRadius: bubbleRadius,
          }}
        >
          {isLoading && liveToolLabel && <ToolPill label={liveToolLabel} followedByText={!!messageText} />}
          {renderBubbleContents({ isLoading, isUser, messageText, renderedHtml, liveToolLabel })}
          {!isUser && !isLoading && message.sources && message.sources.length > 0 && (
            <SourceChips sources={message.sources} />
          )}
        </div>

        {isError && onRetry && (
          <button
            type="button"
            onClick={() => onRetry(message)}
            style={{
              alignSelf: 'flex-start',
              marginTop: '4px',
              padding: '4px 10px',
              borderRadius: theme.borderRadius.full,
              border: `${theme.borderWidth.thin} solid ${theme.colors.border.error}`,
              backgroundColor: 'transparent',
              color: theme.colors.text.error,
              fontSize: theme.typography.fontSize.xs,
              fontWeight: theme.typography.fontWeight.medium,
              cursor: 'pointer',
              transition: `all ${theme.animation.duration.fast}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.colors.text.error}14`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Retry
          </button>
        )}

        {/* Timestamp */}
        <span
          style={utils.merge(styles.text({ variant: 'tertiary', size: 'xs' }), {
            marginLeft: isUser ? 'auto' : theme.spacing['1'],
            marginRight: isUser ? theme.spacing['1'] : 'auto',
          })}
        >
          {formatMessageTime(message.createdAt)}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// Chip Strips — Shadow-DOM-safe inline-style versions of BrainFollowUpChips.
// Clarify variant blocks the user with a question + grounded choices.
// Explore variant offers subtle follow-up suggestions after a complete answer.
// ============================================================================

const ChevronIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 12 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const QuestionIcon: React.FC<{ color: string; size?: number }> = ({ color, size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ClarifyCard: React.FC<{
  chips: CognitionChip[];
  questionText?: string;
  onChipClick: (prompt: string) => void;
}> = ({ chips, questionText, onChipClick }) => {
  const { theme, primaryColor } = useEmbedTheme();
  return (
    <div
      role="group"
      aria-label="Clarifying question"
      style={{
        marginTop: theme.spacing['1'],
        marginLeft: theme.spacing['2'],
        marginRight: theme.spacing['2'],
        padding: '12px',
        borderRadius: theme.borderRadius.lg,
        border: `${theme.borderWidth.thin} solid ${primaryColor}55`,
        backgroundColor: `${primaryColor}0d`,
        animation: 'embed-clarify-pulse 2.4s ease-in-out 1',
      }}
    >
      {questionText ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
            marginBottom: '10px',
          }}
        >
          <span style={{ flexShrink: 0, marginTop: '2px' }}>
            <QuestionIcon color={primaryColor} />
          </span>
          <span
            style={{
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text.link,
              lineHeight: theme.typography.lineHeight.snug,
            }}
          >
            {questionText}
          </span>
        </div>
      ) : (
        <p
          style={{
            margin: '0 0 8px',
            fontSize: '10px',
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.text.link,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Choose one
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {chips.map((chip) => (
          <button
            key={chip.chip_id}
            type="button"
            aria-label={chip.label}
            onClick={() => {
              recordPublicChipTap(chip.chip_id);
              onChipClick(chip.prompt);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '10px 12px',
              borderRadius: theme.borderRadius.md,
              backgroundColor: theme.colors.background.surface,
              border: `${theme.borderWidth.thin} solid ${primaryColor}33`,
              color: theme.colors.text.primary,
              fontSize: theme.typography.fontSize.sm,
              textAlign: 'left',
              cursor: 'pointer',
              transition: `all ${theme.animation.duration.fast}`,
              boxShadow: theme.shadow.sm,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.backgroundColor = `${primaryColor}14`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${primaryColor}33`;
              e.currentTarget.style.backgroundColor = theme.colors.background.surface;
            }}
          >
            <span style={{ flex: 1, lineHeight: theme.typography.lineHeight.snug }}>{chip.label}</span>
            <span style={{ flexShrink: 0, opacity: 0.6 }} aria-hidden="true">
              <ChevronIcon color={primaryColor} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const ExploreStrip: React.FC<{
  chips: CognitionChip[];
  onChipClick: (prompt: string) => void;
}> = ({ chips, onChipClick }) => {
  const { theme, primaryColor } = useEmbedTheme();
  return (
    <div
      role="group"
      aria-label="Suggested follow-ups"
      style={{
        marginTop: theme.spacing['1'],
        marginLeft: theme.spacing['2'],
        marginRight: theme.spacing['2'],
        paddingLeft: '10px',
        borderLeft: `2px solid ${primaryColor}33`,
      }}
    >
      <p
        style={{
          margin: '0 0 6px',
          fontSize: '11px',
          fontWeight: theme.typography.fontWeight.medium,
          color: theme.colors.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
        }}
      >
        or try
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {chips.map((chip) => (
          <button
            key={chip.chip_id}
            type="button"
            aria-label={chip.label}
            onClick={() => {
              recordPublicChipTap(chip.chip_id);
              onChipClick(chip.prompt);
            }}
            style={{
              padding: '4px 10px',
              borderRadius: theme.borderRadius.full,
              backgroundColor: theme.colors.background.surface,
              border: `${theme.borderWidth.thin} solid ${primaryColor}33`,
              color: theme.colors.text.secondary,
              fontSize: '12px',
              fontWeight: theme.typography.fontWeight.medium,
              cursor: 'pointer',
              transition: `all ${theme.animation.duration.fast}`,
              boxShadow: theme.shadow.sm,
              maxWidth: '100%',
              whiteSpace: 'normal',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.color = theme.colors.text.link;
              e.currentTarget.style.backgroundColor = `${primaryColor}0d`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = `${primaryColor}33`;
              e.currentTarget.style.color = theme.colors.text.secondary;
              e.currentTarget.style.backgroundColor = theme.colors.background.surface;
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const EmbedConversationContainer: React.FC<EmbedConversationContainerProps> = ({
  messages,
  mediaBaseUrl,
  cognitionName,
  cognitionImage,
  chips,
  chipsIntent,
  clarifyingQuestionText,
  onChipClick,
  liveToolLabel,
  onRetry,
}) => {
  const { theme, primaryColor } = useEmbedTheme();
  const { styles: advancedStyles, config: advancedConfig } = useAdvancedConfig();
  const cursorChar = advancedConfig.loadingIndicator?.streaming?.cursorChar ?? '▋';

  const lastMessage = messages[messages.length - 1];
  const { bottomAnchorRef } = useStickToBottom({
    tail: lastMessage ? { id: lastMessage._id, textLength: lastMessage.message?.length ?? 0 } : undefined,
    count: messages.length,
  });

  const subtleBg = theme.colors.background.subtle;
  const subtleBorder = theme.colors.border.subtle;
  const surfaceBg = theme.colors.background.surface;
  const defaultBorder = theme.colors.border.default;
  const secondaryText = theme.colors.text.secondary;
  const linkColor = theme.colors.text.link;
  const markdownStyles = useMemo(
    () => `
      .embed-msg-content { overflow-wrap: anywhere; word-break: break-word; }
      .embed-msg-content p { margin: 0; }
      .embed-msg-content p + p { margin-top: 0.5em; }
      .embed-msg-content ul, .embed-msg-content ol { margin: 0.5em 0; padding-left: 1.25em; }
      .embed-msg-content li { margin: 0.25em 0; }
      .embed-msg-content li > p { margin: 0; }
      .embed-msg-content h1, .embed-msg-content h2, .embed-msg-content h3,
      .embed-msg-content h4, .embed-msg-content h5, .embed-msg-content h6 {
        margin: 0.6em 0 0.3em; font-weight: 600; line-height: 1.3;
      }
      .embed-msg-content h1 { font-size: 1.25em; }
      .embed-msg-content h2 {
        font-size: 1.15em;
        margin-top: 0.9em;
        padding-bottom: 0.25em;
        border-bottom: 1px solid ${subtleBorder};
      }
      .embed-msg-content h3 { font-size: 1.05em; margin-top: 0.75em; }
      .embed-msg-content h4, .embed-msg-content h5, .embed-msg-content h6 { font-size: 1em; }
      .embed-msg-content a {
        color: ${linkColor};
        text-decoration: underline;
        text-underline-offset: 2px;
        text-decoration-thickness: 1px;
      }
      .embed-msg-content a:hover { text-decoration-thickness: 2px; }
      .embed-msg-content a.ai-md-extlink::after {
        content: '↗';
        margin-left: 2px;
        font-size: 0.85em;
        opacity: 0.6;
      }
      .embed-msg-content code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 0.9em;
        padding: 1px 4px;
        background: ${subtleBg};
        border-radius: 3px;
      }
      .embed-msg-content pre {
        margin: 0.5em 0;
        padding: 8px 10px;
        /* Lift the code-fence above the bubble bg with a contrasting
           surface + visible border — keeps ASCII art legible whether the
           bubble itself is white, tinted, or dark. */
        background: ${surfaceBg};
        border: 1px solid ${defaultBorder};
        border-radius: 6px;
        overflow-x: auto;
        max-width: 100%;
        /* Tight line-height so ASCII art / box drawing aligns. */
        line-height: 1.35;
      }
      .embed-msg-content pre code { padding: 0; background: transparent; font-size: 0.85em; }
      .embed-msg-content blockquote {
        margin: 0.5em 0;
        padding: 4px 0 4px 10px;
        border-left: 2px solid ${primaryColor};
        color: ${secondaryText};
      }
      .embed-msg-content blockquote > :last-child { margin-bottom: 0; }
      .embed-msg-content hr {
        border: 0;
        border-top: 1px solid ${subtleBorder};
        margin: 0.75em 0;
      }
      .embed-msg-content img { max-width: 100%; height: auto; border-radius: 4px; }
      .embed-msg-content table {
        display: block;
        max-width: 100%;
        overflow-x: auto;
        border: 1px solid ${subtleBorder};
        border-radius: 6px;
        border-collapse: collapse;
        margin: 0.75em 0;
        font-size: 0.9em;
        -webkit-overflow-scrolling: touch;
      }
      .embed-msg-content th, .embed-msg-content td {
        padding: 6px 10px;
        text-align: left;
        vertical-align: top;
        border-bottom: 1px solid ${subtleBorder};
      }
      .embed-msg-content tbody tr:last-child td { border-bottom: 0; }
      .embed-msg-content tbody tr:nth-child(even) { background: ${subtleBg}; }
      .embed-msg-content th { background: ${subtleBg}; font-weight: 600; }
      .embed-msg-content--streaming > :last-child::after {
        content: '${cursorChar}';
        display: inline-block;
        margin-left: 2px;
        color: ${primaryColor};
        animation: embed-caret 1s steps(1, end) infinite;
      }
      @keyframes embed-caret { 50% { opacity: 0; } }
      @keyframes embed-clarify-pulse {
        0%, 100% { box-shadow: 0 0 0 0 ${primaryColor}40; }
        50% { box-shadow: 0 0 0 4px ${primaryColor}00; }
      }
      @media (max-width: 480px) {
        .embed-msg-content table, .embed-msg-content pre { font-size: 0.85em; }
      }
    `,
    [primaryColor, linkColor, subtleBg, subtleBorder, surfaceBg, defaultBorder, secondaryText, cursorChar],
  );

  if (messages.length === 0) {
    return null;
  }

  const showChips = !!onChipClick && !!chips && chips.length > 0 && chipsIntent && chipsIntent !== 'none';

  return (
    <div
      style={{
        ...advancedStyles.containerSpacing,
        ...advancedStyles.typography,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{markdownStyles}</style>
      {messages.map((message, i) => {
        const isLastLoading = message.loading && i === messages.length - 1;
        return (
          <MessageBubble
            key={message._id}
            message={message}
            mediaBaseUrl={mediaBaseUrl}
            cognitionName={cognitionName}
            cognitionImage={cognitionImage}
            liveToolLabel={isLastLoading ? liveToolLabel : null}
            onRetry={onRetry}
          />
        );
      })}
      {showChips && chipsIntent === 'clarify' && (
        <ClarifyCard chips={chips!} questionText={clarifyingQuestionText} onChipClick={onChipClick!} />
      )}
      {showChips && chipsIntent === 'explore' && <ExploreStrip chips={chips!} onChipClick={onChipClick!} />}
      <div ref={bottomAnchorRef} />
    </div>
  );
};

export default EmbedConversationContainer;
